export const groupReviewThreads = (comments = []) => {
  const rows = [...comments].sort((a, b) => (a.id || 0) - (b.id || 0));
  const byId = new Map(rows.map((row) => [row.id, { ...row, replies: [] }]));
  const roots = [];
  rows.forEach((row) => {
    const current = byId.get(row.id);
    if (row.parent_id && byId.has(row.parent_id)) {
      byId.get(row.parent_id).replies.push(current);
      return;
    }
    if (row.parent_id) {
      const parent = rows.find((item) => item.id === row.parent_id);
      if (parent && byId.has(parent.id)) {
        byId.get(parent.id).replies.push(current);
        return;
      }
    }
    roots.push(current);
  });
  return roots;
};

export const threadReviewId = (thread) =>
  thread?.review_id || thread?.reviewId || null;

const normalizeExcerpt = (value) =>
  String(value || '')
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

export const captureEditorSelection = (editor, wrapper) => {
  if (!editor) {
    return null;
  }
  const { from, to } = editor.state.selection;
  const excerpt = String(editor.state.doc.textBetween(from, to, ' ') || '')
    .replace(/\s+/g, ' ')
    .trim();
  const plain = String(editor.getText() || '')
    .replace(/\s+/g, ' ')
    .trim();
  const start = excerpt ? plain.indexOf(excerpt) : -1;
  let top = 48;
  let left = 8;
  try {
    const coords = editor.view.coordsAtPos(from);
    const wrapRect = wrapper?.getBoundingClientRect?.();
    if (wrapRect) {
      top = coords.bottom - wrapRect.top + (wrapper.scrollTop || 0) + 8;
      left = Math.max(8, coords.left - wrapRect.left);
    } else if (typeof window !== 'undefined') {
      top = coords.bottom + window.scrollY + 8;
      left = coords.left + window.scrollX;
    }
  } catch {
    /* coordsAtPos can throw on an unmounted view */
  }
  return {
    excerpt,
    start_offset: start >= 0 ? start : null,
    end_offset: start >= 0 ? start + excerpt.length : null,
    top,
    left,
    hasSelection: from !== to && Boolean(excerpt),
  };
};

const positionInWrapper = (rect, wrapper) => {
  const wrapRect = wrapper?.getBoundingClientRect?.();
  if (wrapRect) {
    return {
      top: rect.bottom - wrapRect.top + (wrapper.scrollTop || 0),
      left: rect.left - wrapRect.left + (wrapper.scrollLeft || 0),
    };
  }
  if (typeof window === 'undefined') {
    return { top: rect.bottom, left: rect.left };
  }
  return {
    top: rect.bottom + window.scrollY,
    left: rect.left + window.scrollX,
  };
};

export const captureTextSelection = (container, wrapper) => {
  if (!container || typeof window === 'undefined') {
    return null;
  }
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || !selection.rangeCount) {
    return null;
  }
  const range = selection.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) {
    return null;
  }
  const excerpt = normalizeExcerpt(selection.toString());
  if (!excerpt) {
    return null;
  }
  const plain = normalizeExcerpt(container.innerText || '');
  const start = plain.indexOf(excerpt);
  const rect = range.getBoundingClientRect();
  const position = positionInWrapper(rect, wrapper || container);
  return {
    excerpt,
    start_offset: start >= 0 ? start : null,
    end_offset: start >= 0 ? start + excerpt.length : null,
    top: position.top,
    left: position.left,
  };
};

const collectTextNodes = (root) => {
  const nodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    nodes.push(node);
    node = walker.nextNode();
  }
  return nodes;
};

const mapOffsetToNode = (nodes, offset) => {
  let remaining = offset;
  for (const node of nodes) {
    const length = (node.nodeValue || '').length;
    if (remaining <= length) {
      return { node, offset: remaining };
    }
    remaining -= length;
  }
  const last = nodes[nodes.length - 1];
  return last ? { node: last, offset: (last.nodeValue || '').length } : null;
};

const findExactRange = (nodes, excerpt) => {
  if (!excerpt) {
    return null;
  }
  const full = nodes.map((node) => node.nodeValue || '').join('');
  const index = full.indexOf(excerpt);
  if (index < 0) {
    return null;
  }
  const start = mapOffsetToNode(nodes, index);
  const end = mapOffsetToNode(nodes, index + excerpt.length);
  if (!start || !end) {
    return null;
  }
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  return range;
};

const findNormalizedRange = (nodes, excerpt) => {
  const needle = normalizeExcerpt(excerpt);
  if (!needle) {
    return null;
  }
  const haystack = [];
  const map = [];
  nodes.forEach((node) => {
    const value = node.nodeValue || '';
    for (let index = 0; index < value.length; index += 1) {
      const character = value[index];
      if (/\s/.test(character)) {
        if (haystack[haystack.length - 1] !== ' ') {
          haystack.push(' ');
          map.push({ node, offset: index });
        }
        continue;
      }
      haystack.push(character);
      map.push({ node, offset: index });
    }
  });
  const text = haystack.join('').trimStart();
  const leadingSpaces = haystack.length - text.length;
  const index = text.indexOf(needle);
  if (index < 0) {
    return null;
  }
  const start = map[index + leadingSpaces];
  const end = map[index + leadingSpaces + needle.length - 1];
  if (!start || !end) {
    return null;
  }
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset + 1);
  return range;
};

const findOffsetRange = (nodes, startOffset, endOffset, excerpt) => {
  if (startOffset == null || endOffset == null || endOffset <= startOffset) {
    return null;
  }
  const full = nodes.map((node) => node.nodeValue || '').join('');
  const slice = full.slice(startOffset, endOffset);
  if (excerpt && normalizeExcerpt(slice) !== normalizeExcerpt(excerpt)) {
    return findNormalizedRange(nodes, excerpt);
  }
  const start = mapOffsetToNode(nodes, startOffset);
  const end = mapOffsetToNode(nodes, endOffset);
  if (!start || !end) {
    return null;
  }
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  return range;
};

const wrapRange = (range, commentId, isActive) => {
  const mark = document.createElement('mark');
  mark.dataset.commentId = String(commentId);
  mark.className = isActive ? 'rw-mark is-active' : 'rw-mark';
  try {
    range.surroundContents(mark);
  } catch {
    const contents = range.extractContents();
    mark.appendChild(contents);
    range.insertNode(mark);
  }
};

const unwrapMarks = (root) => {
  root.querySelectorAll('mark[data-comment-id]').forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) {
      return;
    }
    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark);
    }
    parent.removeChild(mark);
  });
};

const highlightThread = (root, thread, activeId) => {
  const excerpt = normalizeExcerpt(thread?.excerpt);
  if (!excerpt || thread.resolved_at) {
    return false;
  }
  const nodes = collectTextNodes(root).filter(
    (node) => !node.parentElement?.closest('[data-comment-id]')
  );
  if (!nodes.length) {
    return false;
  }
  const range =
    findOffsetRange(nodes, thread.start_offset, thread.end_offset, excerpt) ||
    findExactRange(nodes, excerpt) ||
    findExactRange(nodes, thread.excerpt) ||
    findNormalizedRange(nodes, excerpt);
  if (!range || range.collapsed) {
    return false;
  }
  wrapRange(range, thread.id, Number(activeId) === Number(thread.id));
  return true;
};

export const applyThreadHighlights = (root, threads, activeId) => {
  if (!root) {
    return;
  }
  unwrapMarks(root);
  threads.forEach((thread) => {
    highlightThread(root, thread, activeId);
  });
};
